<template>
  <v-card class="pa-4" flat>
    <custom-popup
      v-model="showLinkModal"
      :cons="{ title: 'اتصال محصول به دیجی‌کالا: ' + selectedProduct?.title, icon: 'link-variant' }"
      max-width="800"
      hide-confirm
      @close="showLinkModal = false"
      @reload="(e) => (rerender = e)"
      reloadable
    >
      <link-product-modal
        v-if="showLinkModal"
        :product="selectedProduct"
        @saved="onProductLinked"
        @close="showLinkModal = false"
      />
    </custom-popup>

    <!-- مدال ساخت لیبل قیمت -->
    <generate-price-label-modal
      v-if="showGenerateQrModal"
      v-model="showGenerateQrModal"
      :product="qrProductData"
      @saved="onQrGenerated"
      @close="showGenerateQrModal = false"
    />

    <!-- بنر زنده نمایش پروسس ساخت کیوآر در همین صفحه -->
    <v-expand-transition>
      <div v-if="activeBgTasks.length > 0" class="mb-4">
        <v-alert
          v-for="task in activeBgTasks"
          :key="task.id"
          color="primary"
          dark
          dense
          class="mb-2 rounded-lg elevation-2"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="white"
                class="ml-2"
              />
              <span class="font-weight-bold">
                در حال ساخت کیوآر: {{ task.title || 'محصول' }}
              </span>
              <span v-if="task.currentTitle" class="text-caption mr-2 opacity-80">
                ({{ task.currentTitle }})
              </span>
            </div>

            <div class="text-caption font-weight-bold d-flex align-center">
              <span class="dir-ltr ml-1">{{ task.current }} / {{ task.total }}</span>
              <span>({{ task.percent }}%)</span>
            </div>
          </div>

          <v-progress-linear
            :value="task.percent"
            height="7"
            rounded
            striped
            color="amber"
          />
        </v-alert>
      </div>
    </v-expand-transition>

    <!-- نوار دکمه‌های بالای جدول -->
    <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-4 gap-2">
      <v-btn
        class="btn-get-code rounded-lg px-6"
        @click="openCreateModal"
      >
        <v-icon left>mdi-plus-box</v-icon>
        تعریف محصول جدید
      </v-btn>
    </div>

    <!-- جدول نمایش محصولات -->
    <vue-table
      :data="products"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
      v-model="selectedRows"
      show-select
    >
      <!-- فیلترهای یکپارچه -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense
              hide-details
              label="تعداد در صفحه"
              outlined
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="3" md="3">
            <v-text-field
              v-model="props.search"
              label="جستجو (عنوان یا SKU)"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
              class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.has_variants"
              :items="variantOptions"
              label="نوع محصول"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.is_active"
              :items="statusOptions"
              label="وضعیت فعالیت"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.in_stock"
              :items="stockOptions"
              label="وضعیت موجودی"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <!-- اسلات ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + (products?.current_page - 1) * products?.per_page) }}
      </template>

      <!-- اسلات عنوان -->
      <template v-slot:title="{ item }">
        <div class="font-weight-bold">{{ item.title }}</div>
        <small class="grey--text">کد کالا: {{ item.sku }}</small>
      </template>
      <template v-slot:mainImage="{ item }">
        <v-img v-if="item.main_image?.url" :src="item.main_image.url" max-width="80" class="rounded-lg mx-auto" />
      </template>

      <!-- اسلات نوع کالا -->
      <template v-slot:has_variants="{ item }">
        <v-chip
          small
          :color="item.has_variants ? 'deep-purple lighten-5' : 'blue lighten-5'"
          :text-color="item.has_variants ? 'deep-purple darken-3' : 'blue darken-3'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.has_variants ? 'mdi-sitemap' : 'mdi-cube-outline' }}</v-icon>
          {{ item.has_variants ? 'دارای تنوع' : 'محصول ساده' }}
        </v-chip>
      </template>

      <!-- اسلات موجودی کل -->
      <template v-slot:stock="{ item }">
        <v-chip
          small
          :color="item.stock > 0 ? 'green lighten-5' : 'red lighten-5'"
          :text-color="item.stock > 0 ? 'green darken-3' : 'red darken-3'"
          class="font-weight-bold"
        >
          {{ persian(item.stock || 0) }} عدد
        </v-chip>
      </template>

      <!-- اسلات آمار تنوع و لیستینگ -->
      <template v-slot:stats="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small outlined color="primary" class="ml-1">
                <v-icon left x-small>mdi-tag-multiple</v-icon>
                {{ persian(item.skus_count || 0) }}
              </v-chip>
            </template>
            <span>تعداد تنوع‌ها (SKU)</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small outlined color="teal">
                <v-icon left x-small>mdi-store-sync</v-icon>
                {{ persian(item.listings_count || 0) }}
              </v-chip>
            </template>
            <span>تعداد لیستینگ‌های متصل</span>
          </v-tooltip>
        </div>
      </template>

      <!-- اسلات وضعیت فعال/غیرفعال -->
      <template v-slot:is_active="{ item }">
        <v-switch
          v-model="item.is_active"
          color="success"
          hide-details
          dense
          class="mt-0 pt-0 d-inline-block"
          @change="toggleProductStatus(item)"
        />
      </template>

      <!-- عملیات -->
      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton(item)" />
      </template>
    </vue-table>

    <!-- مدال فرم ایجاد و ویرایش -->
    <product-form-modal
      v-model="showFormModal"
      :selected-product="selectedProduct"
      @refresh="fetchProducts"
      :isEdit="editMode"
    />

    <!-- مدال مدیریت گروهی قیمت -->
    <bulk-price-modal
      v-model="showBulkPriceModal"
      :selected-items="selectedRows"
      @refresh="onBulkPriceSuccess"
    />
  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import ProductFormModal from "./ProductFormModal.vue";
import PriceInput from "../../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";
import LinkProductModal from "./LinkProductModal.vue"; 
import SmartPricingModal from "../product/SmartPricingModal.vue";
import BulkPriceModal from "./BulkPriceModal.vue";
import GeneratePriceLabelModal from "./GeneratePriceLabelModal.vue";
import { qrQueue } from "@/services/qrQueue";

export default {
  name: "ProductsListTab",
  components: {
    SmartPricingModal,
    LinkProductModal,
    ActionButtons,
    ProductFormModal,
    PriceInput,
    BulkPriceModal,
    GeneratePriceLabelModal
  },
  data() {
    return {
      rerender: true,
      queueTick: 0,
      showLinkModal: false,
      showVariantsModal: false,
      selectedProductId: null,
      selectedProductTitle: '',
      showPricingModal: false,
      selectedListingId: null, 
      loading: false,
      products: [],
      showFormModal: false,
      editMode: false,
      selectedProduct: null,
      selectedRows: [],
      showBulkPriceModal: false,
      showGenerateQrModal: false,
      qrProductData: null,

      variantOptions: [
        { text: "همه", value: null },
        { text: "دارای تنوع", value: 1 },
        { text: "ساده", value: 0 },
      ],
      statusOptions: [
        { text: "همه", value: null },
        { text: "فعال", value: 1 },
        { text: "غیرفعال", value: 0 },
      ],
      stockOptions: [
        { text: "همه", value: null },
        { text: "موجود", value: 1 },
        { text: "ناموجود", value: 0 },
      ],

      tableProps: {
        page: 1,
        length: 10,
        search: null,
        has_variants: null,
        is_active: null,
        in_stock: null,
      },
    };
  },

  computed: {
    activeBgTasks() {
      const _tick = this.queueTick;
      const tasks = qrQueue.tasks || {};
      return Object.values(tasks).filter(
        (t) => t && (t.status === "running" || t.status === "processing")
      );
    },

    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: false, width: "60px" },
        { value: "mainImage", text: "تصویر", align: "center", sortable: true },
        { value: "title", text: "عنوان و SKU", align: "start", sortable: true },
        { value: "has_variants", text: "نوع", align: "center", sortable: false },
        { value: "stock", text: "موجودی کل", align: "center", sortable: true },
        { value: "stats", text: "تنوع / اتصال", align: "center", sortable: false },
        { value: "is_active", text: "وضعیت", align: "center", sortable: false },
        { value: "action", text: "عملیات", align: "center", sortable: false, event: "actions" },
      ];
    },

    currentItems() {
      return this.products?.data || (Array.isArray(this.products) ? this.products : []);
    },
  },

  mounted() {
    this.fetchProducts();
    qrQueue.$on("task-updated", this.onQueueChange);
  },

  beforeDestroy() {
    qrQueue.$off("task-updated", this.onQueueChange);
  },

  methods: {
    onQueueChange() {
      this.queueTick++;
    },

    async generateQr(item) {
      this.loading = true;
      try {
        const { data } = await this.$api.get(`/v1/products/show/${item.id}`);
        const productDetails = data.data || data;

        if (!productDetails.skus || productDetails.skus.length === 0) {
          productDetails.skus = [
            {
              id: productDetails.id,
              sku: productDetails.sku,
              title: productDetails.title,
              consumer_price: productDetails.consumer_price,
              production_cost: productDetails.production_cost,
              price: productDetails.price,
              stock: productDetails.stock,
              qr_id: productDetails.qr_id || null,
            }
          ];
        }

        this.qrProductData = productDetails;
        this.showGenerateQrModal = true;
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در دریافت اطلاعات محصول");
      } finally {
        this.loading = false;
      }
    },

    onQrGenerated() {
      this.showGenerateQrModal = false;
      this.fetchProducts();
    },

    onBulkPriceSuccess() {
      this.selectedRows = [];
      this.fetchProducts();
    },

    metabutton(item) {
      const buttons = [ 
        {
          name: "generate",
          label: 'ساخت لیبل قیمت',
          icon: 'qrcode',
          class: 'btn-buy rounded-pill',
          action: this.actions
        },
        {
          name: "edit",
          label: "ویرایش",
          icon: "pencil-outline",
          class: "btn-edit rounded-pill",
          action: this.actions,
        },
        {
          name: "delete",
          label: "آرشیو",
          icon: "archive-outline",
          class: "btn-delete rounded-pill",
          action: this.actions,
        },
      ];

      buttons.push({
        name: "link",
        label: "اتصال به دیجی‌کالا",
        icon: "link-variant-plus",
        class: "btn-show rounded-pill", 
        action: this.actions,
      });
      
      if (item.has_variants) {
        buttons.unshift({
          name: "manage_variants",
          label: `مدیریت تنوع‌ها (${item.stock})`, 
          icon: "tag-check-outline",
          class: "btn-show rounded-pill",
          action: (row) => this.openVariantsModal(row),
        });
      }

      return { buttons };
    },

    linkProduct(item) {
      this.selectedProduct = item;
      this.showLinkModal = true;
    },

    onProductLinked() {
      this.showLinkModal = false;
      this.fetchProducts();
    },

    async unlinkProduct(item) {
      const listingId = item.listing_id || item.default_listing_id || item.id; 

      const confirm = await this.$modal.yesNo(
        `آیا از قطع اتصال محصول "${item.title}" از دیجی‌کالا اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.post(`/v1/marketplace-listings/${listingId}/unlink`);
        this.$toast.success("اتصال محصول با موفقیت قطع شد.");
        this.fetchProducts();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در قطع اتصال محصول");
      } finally {
        this.loading = false;
      }
    },

    openVariantsModal(product) {
      this.selectedProductId = product.id;
      this.selectedProductTitle = product.title;
      this.$router.push({ name: 'ProductSkusManager', params: { productId: product.id, productsku: product.sku } });
    },

    openPricingModal(item) {
      const listingId = item.listing_id || item.default_listing_id || item.id;
      this.selectedListingId = listingId;
      this.showPricingModal = true;
    },

    async fetchProducts() {
      this.loading = true;
      const params = {};
      Object.keys(this.tableProps).forEach((key) => {
        if (this.tableProps[key] !== null && this.tableProps[key] !== "") {
          params[key] = this.tableProps[key];
        }
      });

      try {
        const { data } = await this.$api.get("/v1/products?trashed=false", { params });
        this.products = data.data || data;
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست محصولات");
      } finally {
        this.loading = false;
      }
    },

    onPagination(e) {
      if (e && typeof e === "object" && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e };
        if ("search" in e || "has_variants" in e || "is_active" in e || "in_stock" in e) {
          this.tableProps.page = 1;
        }
      }
      this.fetchProducts();
    },

    actions(item, type) {
      if (type === "edit") {
        this.selectedProduct = item;
        this.editMode = true;
        this.showFormModal = true;
      } else if (type === "delete") {
        this.deleteProduct(item);
      } else if (type === "link") {
        this.linkProduct(item);
      } else if (type === "unlink") {
        this.unlinkProduct(item);
      } else if (type === 'generate') {
        this.generateQr(item);
      }
    },

    openCreateModal() {
      this.selectedProduct = null;
      this.editMode = false;
      this.showFormModal = true;
    },

    onProductSaved() {
      this.showFormModal = false;
      this.fetchProducts();
    },

    async toggleProductStatus(item) {
      try {
        await this.$api.post(`/v1/products/${item.id}`, {
          is_active: item.is_active,
        });
        this.$toast.success("وضعیت محصول بروزرسانی شد");
      } catch (e) {
        item.is_active = !item.is_active;
        this.$toast.error("خطا در تغییر وضعیت محصول");
      }
    },

    async deleteProduct(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از حذف محصول "${item.title}" و کلیه تنوع‌های آن اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/products/${item.id}`);
        this.$toast.success("محصول با موفقیت حذف گردید");
        this.fetchProducts();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در حذف محصول");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-2 { gap: 8px; }
.opacity-80 { opacity: 0.85; }
.dir-ltr { direction: ltr; display: inline-block; }
</style>
